import { UiState } from 'instantsearch.js';
import { history } from 'instantsearch.js/es/lib/routers';
import { searchIndexes } from '../../search-shared/client';

type RouteState = Partial<{
  query: string,
  page: number,
  sort: string,
  category: string[],
  type: string[],
  room: string[],
  colour: string[],
  brand: string[],
  designer: string[],
  range: string[],
  // Toggle refinements
  bestSellers: boolean,
  commercial: boolean,
  transactableOnline: boolean,
  inStockAndReadyToShip: boolean,
  onSale: boolean,
}>

const compactObject = (obj: Record<string, any>) => {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, v]) => v != null)
  );
}

const normalizeArrayValues = (obj: Record<string, any>, keys: string[]): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      keys.includes(key) ? (Array.isArray(value) ? value : value ? [value] : []) : value
    ])
  );
};

const originalWindowTitle = document.title;

export const makeRouting = (searchIndex: string) => ({
  router: history<RouteState>({
    cleanUrlOnDispose: true,
    
    windowTitle({ category, query }) {
      const queryTitle = query ? `Results for "${query}"` : '';
  
      return [queryTitle, category, originalWindowTitle]
        .filter(Boolean)
        .join(' | ');
    },

    createURL({ routeState }) {
      const { query, page, ...filters } = routeState;
      const searchParams = new URLSearchParams();

      if (query) {
        searchParams.append('query', query);
      }
      
      if (page !== undefined && page !== 1) {
        searchParams.append('page', page.toString());
      }

      Object.entries(filters).forEach(([key, values]) => {
        if (Array.isArray(values)) {
          values.forEach(value => searchParams.append(key, value));
        } else if (typeof values === 'string') {
          searchParams.append(key, values);
        } else if (typeof values === 'boolean' && values === true) {
          searchParams.append(key, values.toString());
        }
      });

      const searchString = searchParams.toString();
      const baseURL = `${location.origin}${location.pathname}`;

      return searchString.length ? `${baseURL}?${searchString}` : baseURL;
    },

    parseURL({ location }) {
      const searchParams = new URLSearchParams(location.search);
      const routeState: Record<string, any> = {};

      searchParams.forEach((value, key) => {
        // console.log(`Parsing URL parameter: ${key} = ${value}`, {routeState});
        if (routeState[key]) {
          if (Array.isArray(routeState[key])) {
            routeState[key].push(value);
          } else {
            routeState[key] = [routeState[key], value];
          }
        } else {
          switch (key) {
            case 'page':
              routeState[key] = Number(value);
              break;
            case 'bestSellers':
            case 'commercial':
            case 'transactableOnline':
            case 'inStockAndReadyToShip':
            case 'onSale':
              routeState[key] = value === 'true';
              break;
            default:
              routeState[key] = value;
          }
        }
      });

      return routeState;
    },
  }),

  stateMapping: {
    stateToRoute(uiState: UiState): RouteState {      
      const indexUiState = uiState[searchIndex] || {};

      const routeState = {
        query: indexUiState.query,
        page: indexUiState.page,
        sort: indexUiState.sortBy,

        category: indexUiState.refinementList?.["named_tags.category"],
        type: indexUiState.refinementList?.product_type,
        room: indexUiState.refinementList?.["meta.custom.rooms"],
        colour: indexUiState.refinementList?.["meta.custom.colours"],
        brand: indexUiState.refinementList?.vendor,
        designer: indexUiState.refinementList?.["named_tags.designer"],
        range: indexUiState.refinementList?.["named_tags.range"],

        // Toggle refinements
        bestSellers: indexUiState.toggle?.["meta.custom.best_seller"],
        commercial: !!indexUiState.refinementList?.["tags"]?.includes("Commercial"),
        transactableOnline: !!indexUiState.refinementList?.["named_tags.availability"]?.includes("Shop online"),
        inStockAndReadyToShip: !!indexUiState.numericMenu?.["inventory_quantity"],
        onSale: !!indexUiState.refinementList?.["named_tags_names"]?.includes("sale"),
      };

      return routeState;
    },

    routeToState(routeState: RouteState): UiState {
      // console.log("routeToState.routeState ", routeState);

      const state = {
        [searchIndex]: {
          query: routeState.query,
          page: routeState.page !== undefined ? Number(routeState.page) : undefined,
          sortBy: routeState.sort,

          refinementList: compactObject(
            normalizeArrayValues(
              {
                "named_tags.category": routeState?.category,
                product_type: routeState?.type,
                "meta.custom.rooms": routeState?.room,
                "meta.custom.colours": routeState?.colour,
                vendor: routeState?.brand,
                "named_tags.designer": routeState?.designer,
                "named_tags.range": routeState?.range,
                "tags": [
                  ...(routeState.commercial ? ["Commercial"] : [])
                ],
                "named_tags.availability": [
                  ...(routeState.transactableOnline ? ["Shop online"] : [])
                ],
                "named_tags_names": [
                  ...(routeState.onSale ? ["sale"] : [])
                ]
              },
              [
                "named_tags.category",
                "product_type",
                "meta.custom.rooms",
                "meta.custom.colours",
                "vendor",
                "named_tags.designer",
                "named_tags.range",
                // Toggle refinements, TODO: refactor named_tags_names to use <RefinementListToggle> to avoid repeating the attribute, like `tags`
                "tags",
                "named_tags.availability",
                "named_tags_names"
              ]
            )
          ),

          toggle: compactObject({
            "meta.custom.best_seller": routeState.bestSellers,
          }),

          numericMenu: compactObject({
            "inventory_quantity": routeState.inStockAndReadyToShip ? "1:" : undefined,
          }),
        },
      };

      return state;
    },
  },
});

export const productsRouting = makeRouting(searchIndexes.RECOMMENDED_PRODUCTS);
export const collectionProductsRouting = makeRouting(searchIndexes.RECOMMENDED_PRODUCTS);

export default productsRouting;